import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useContent } from "@/content";
import { media } from "@/content/media";
import { useSeo, useNow } from "@/lib/hooks";
import { Section, Photo } from "@/components/Layout";
import { Reveal, Eyebrow, MaskedTitle, GoldLine } from "@/components/Motion";
import { Countdown } from "@/components/Countdown";
import { NotifyForm } from "@/components/Forms";
import { CategoriesList } from "@/sections/Categories";
import { FinalistsSection, WinnersSection, ProductGrid, StatusBanner } from "@/sections/Showcase";
import { FinalCall } from "@/sections/FinalCall";

const PageHero = ({ eyebrow, title, intro, children, testid, image }) => (
  <Section tone="aubergine" className="pb-16 pt-36 md:pt-44">
    <div className={image ? "grid items-end gap-12 lg:grid-cols-[1.1fr_0.9fr]" : ""}>
      <div>
        <Reveal><Eyebrow>{eyebrow}</Eyebrow></Reveal>
        <MaskedTitle as="h1" className="mt-6 font-serif text-[2rem] leading-[1.15] md:text-[3rem]" data-testid={testid}>{title}</MaskedTitle>
        {intro && <Reveal delay={0.15}><p className="mt-6 max-w-2xl leading-relaxed text-white/80">{intro}</p></Reveal>}
        {children && <Reveal delay={0.2} className="mt-8">{children}</Reveal>}
      </div>
      {image && <Reveal><Photo src={image.src} alt={image.alt} className="aspect-[16/10]" /></Reveal>}
    </div>
  </Section>
);

export const CategoriesPage = () => {
  const c = useContent();
  useSeo(c.seo.categories);
  return (
    <>
      <PageHero eyebrow={c.pages.categories.eyebrow} title={c.pages.categories.title} testid="categories-page-title" image={{ src: media.categories, alt: "Texture douce, tons chauds" }} />
      <Section tone="night" className="pb-28 md:pb-36">
        <CategoriesList />
        <Reveal className="mt-16">
          <Link to="/candidater" data-testid="categories-apply" className="group inline-flex items-center gap-3 rounded-[5px] bg-copper px-8 py-4 text-[0.75rem] uppercase tracking-[0.3em] text-white transition-opacity hover:opacity-90">{c.ui.apply} <ArrowUpRight size={16} strokeWidth={1.4} /></Link>
        </Reveal>
      </Section>
    </>
  );
};

export const FinalistsPage = () => {
  const c = useContent();
  useSeo(c.seo.finalists);
  return (
    <>
      <PageHero eyebrow={c.pages.finalists.eyebrow} title={c.pages.finalists.title} testid="finalists-page-title"><StatusBanner testid="finalists-page-status">{c.finalists.status}</StatusBanner></PageHero>
      <Section tone="aubergine" className="pb-24"><ProductGrid products={c.finalists.products} testid="finalists-grid" /></Section>
      <FinalistsSection full />
    </>
  );
};

export const WinnersPage = () => {
  const c = useContent();
  useSeo(c.seo.winners);
  return (
    <>
      <PageHero eyebrow={c.pages.winners.eyebrow} title={c.pages.winners.title} testid="winners-page-title"><StatusBanner testid="winners-page-status">{c.winners.status}</StatusBanner></PageHero>
      <Section tone="aubergine" className="pb-24"><ProductGrid products={c.winners.products} testid="winners-grid" /></Section>
      <WinnersSection full />
    </>
  );
};

export const ApplyPage = () => {
  const c = useContent();
  useSeo(c.seo.apply);
  const a = c.applyPage;
  const isOpen = useNow(15000) >= new Date(c.site.openingDate).getTime();
  return (
    <Section tone="aubergine" className="min-h-[100svh] pb-28 pt-36 md:pt-44" testid="apply-page">
      <div className="grid gap-16 lg:grid-cols-[1.1fr_0.9fr] lg:gap-28">
        <div>
          <Reveal><Eyebrow>{a.eyebrow}</Eyebrow></Reveal>
          <MaskedTitle as="h1" className="mt-6 font-serif text-[2rem] leading-[1.15] md:text-[3rem]" data-testid={isOpen ? "apply-open-title" : "apply-closed-title"}>{isOpen ? a.openTitle : a.closedTitle}</MaskedTitle>
          <GoldLine className="my-10 max-w-[140px]" />
          <Reveal><p className="text-lg text-white/85" data-testid="apply-text">{isOpen ? a.openText : a.closedText}</p></Reveal>
          {isOpen ? (
            <Reveal delay={0.1} className="mt-10">
              <a href={c.site.applyUrl} target="_blank" rel="noopener noreferrer" data-testid="apply-external-button" className="group inline-flex items-center gap-3 rounded-[5px] bg-copper px-9 py-5 text-[0.75rem] uppercase tracking-[0.3em] text-white transition-opacity hover:opacity-90">
                {a.openButton} <ArrowUpRight size={16} strokeWidth={1.4} className="transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              <p className="mt-4 text-sm text-white/85">{c.site.applyDomain}</p>
            </Reveal>
          ) : (
            <Reveal delay={0.1} className="mt-12"><Countdown /></Reveal>
          )}
        </div>
        {!isOpen && (
          <Reveal className="self-end rounded-[5px] border border-white/25 p-8 md:p-12" data-testid="apply-notify-card">
            <h2 className="mb-8 font-serif text-2xl md:text-[1.75rem]">{c.forms.notify.title}</h2>
            <NotifyForm />
          </Reveal>
        )}
      </div>
    </Section>
  );
};

export const ContactPage = () => {
  useSeo(useContent().seo.contact);
  return <FinalCall pageMode />;
};

export const NotFound = () => {
  const c = useContent();
  return (
    <Section tone="aubergine" className="min-h-[80svh] pt-44" testid="not-found">
      <h1 className="font-serif text-[2rem] md:text-[3rem]">{c.notFound.title}</h1>
      <Link to="/" className="link-line mt-8 inline-block text-[0.75rem] uppercase tracking-[0.3em] text-copper">{c.notFound.back}</Link>
    </Section>
  );
};
